import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, ShieldCheck, Zap, Users, ArrowRight,
  CheckCircle2, Globe, Wallet, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { fmtDate, fmtMoney } from "@/utils/format";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import StatusBadge from "@/components/StatusBadge";
import WhatsAppAssistButton from "@/components/WhatsAppAssistButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function fill(str, vars = {}) {
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replaceAll(`{{${k}}}`, String(v)),
    str || ""
  );
}

export default function TrackingPage() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runTrack = async (rawId) => {
    const tid = (rawId || "").trim().replace(/^#+/, "").toUpperCase();
    if (!tid) return;
    setId(tid);
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/track/${tid}`);
      setResult(data);
      setTimeout(() => document.getElementById("result-anchor")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("track_not_found"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get("id") || searchParams.get("tg");
    if (q) runTrack(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    await runTrack(id);
  };

  const titleParts = fill(t("hero_title"), { brand: "\u0000" }).split("\u0000");
  const trustItems = [
    { icon: CheckCircle2, text: t("trust_same_day") },
    { icon: ShieldCheck, text: t("trust_secure") },
    { icon: Globe, text: t("trust_network") },
  ];
  const logoStats = [
    { v: "24h", l: t("stat_delivery") },
    { v: "3%", l: t("stat_flat_fee") },
    { v: "🇭🇹", l: t("stat_local") },
  ];
  const features = [
    { icon: Zap, title: t("feat_fast_title"), desc: t("feat_fast_desc") },
    { icon: ShieldCheck, title: t("feat_secure_title"), desc: t("feat_secure_desc") },
    { icon: Users, title: t("feat_agents_title"), desc: t("feat_agents_desc") },
    { icon: TrendingUp, title: t("feat_realtime_title"), desc: t("feat_realtime_desc") },
  ];
  const steps = [
    { n: "01", title: t("how_step1_title"), desc: t("how_step1_desc") },
    { n: "02", title: t("how_step2_title"), desc: t("how_step2_desc") },
    { n: "03", title: t("how_step3_title"), desc: t("how_step3_desc") },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-brand-blue/25 blur-[120px]" />
        <div className="absolute top-[30%] -left-40 w-[520px] h-[520px] rounded-full bg-brand-gold/15 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.07]"
          style={{
            backgroundImage: "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-20 flex items-center justify-between px-3 sm:px-8 py-4 sm:py-5 gap-2">
        <Logo size={40} />
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Link to="/agent">
            <Button variant="ghost" size="sm" data-testid="agent-portal-link" className="font-semibold px-2 sm:px-3 text-xs sm:text-sm">
              {t("agent")}
            </Button>
          </Link>
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-16 pb-24">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          {/* Left column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3.5 py-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em]"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>{t("hero_badge")}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-6 font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-foreground"
            >
              {titleParts[0]}
              <span className="relative inline-block">
                <span className="relative z-10 text-brand-blue dark:text-blue-400">Zelle</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-brand-gold/40 -z-0 rounded-sm" />
              </span>
              {titleParts[1] || ""}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              {t("hero_subtitle")}
            </motion.p>

            {/* Search card */}
            <motion.form
              onSubmit={handleSearch}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8"
            >
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">
                {t("track_title")}
              </div>
              <div className="glass rounded-2xl p-2 flex gap-2 items-center border-2 border-brand-blue/20 dark:border-brand-blue/40 shadow-2xl shadow-brand-blue/10">
                <div className="pl-3 text-brand-blue dark:text-blue-400">
                  <Search className="h-5 w-5" />
                </div>
                <Input
                  value={id}
                  onChange={(e) => setId(e.target.value.toUpperCase())}
                  placeholder={t("track_placeholder")}
                  data-testid="track-input"
                  className="border-0 bg-transparent text-lg font-mono focus-visible:ring-0 h-12 tracking-wider"
                  maxLength={12}
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="track-submit-btn"
                  className="h-12 px-5 sm:px-6 rounded-xl bg-brand-blue hover:bg-brand-blueHover text-white font-bold gap-2"
                >
                  {loading ? t("loading") : (
                    <>
                      <span className="hidden sm:inline">{t("track_btn")}</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("track_example")} <span className="font-mono text-foreground">TG48591234</span>
              </p>
            </motion.form>

            {/* Trust points */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-4 text-sm text-muted-foreground"
            >
              {trustItems.map((it) => (
                <div key={it.text} className="flex items-center gap-2">
                  <it.icon className="h-4 w-4 text-brand-gold" />
                  <span className="font-medium">{it.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right column — Logo showcase card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-brand-blue/30 via-brand-gold/20 to-transparent blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-border bg-card p-10 shadow-2xl">
                <div className="flex items-center justify-center py-6">
                  <img
                    src="/tgs-logo.png"
                    alt={t("logo_alt")}
                    className="w-64 h-64 object-contain drop-shadow-2xl"
                  />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  {logoStats.map((s) => (
                    <div key={s.l} className="rounded-xl bg-secondary/40 p-3">
                      <div className="font-heading font-bold text-xl text-foreground">{s.v}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-semibold">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Result */}
        <div id="result-anchor" />
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 mx-auto max-w-3xl"
            data-testid="track-result-card"
          >
            <div className="rounded-3xl border-2 border-brand-blue/30 bg-card p-6 sm:p-8 shadow-2xl">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">{t("result_transfer_id")}</div>
                  <div className="text-3xl font-mono font-extrabold text-foreground mt-1" data-testid="result-transfer-id">
                    {result.transfer_id}
                  </div>
                </div>
                <StatusBadge status={result.status} testId="result-status" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="rounded-2xl bg-brand-blue/5 border border-brand-blue/20 p-5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t("amount")}</div>
                  <div className="text-2xl font-heading font-extrabold text-foreground mt-1" data-testid="result-amount">
                    {fmtMoney(result.amount)}
                  </div>
                </div>
                <div className="rounded-2xl bg-secondary/50 p-5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t("result_created_at")}</div>
                  <div className="text-sm font-semibold text-foreground mt-1">{fmtDate(result.created_at)}</div>
                </div>
              </div>
              {result.delivered_at && (
                <div className="mt-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold">{t("result_delivered_at")}</div>
                    <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mt-0.5">{fmtDate(result.delivered_at)}</div>
                  </div>
                </div>
              )}
              {result.status === "canceled" && (
                <div className="mt-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 p-5 space-y-3" data-testid="track-cancel-info">
                  {result.cancel_reason && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-rose-600 dark:text-rose-400 font-bold">{t("result_cancel_reason")}</div>
                      <div className="text-sm font-semibold text-foreground mt-1">{result.cancel_reason}</div>
                    </div>
                  )}
                  {result.cancel_resolution && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-rose-600 dark:text-rose-400 font-bold">{t("result_cancel_solution")}</div>
                      <div className="text-sm text-foreground mt-1 leading-relaxed">
                        {result.cancel_resolution === "refund"
                          ? t("cancel_solution_refund")
                          : t("cancel_solution_reactivate")}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {result.reactivated_at && result.status !== "canceled" && (
                <div className="mt-4 rounded-2xl bg-sky-500/10 border border-sky-500/30 p-5">
                  <div className="text-xs uppercase tracking-widest text-sky-600 dark:text-sky-400 font-bold">{t("result_reactivated")}</div>
                  <div className="text-sm font-semibold text-foreground mt-1">{fmtDate(result.reactivated_at)}</div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Happy family */}
        <section className="mt-24 sm:mt-32">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3.5 py-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em]">
              <span>❤</span> {t("family_badge")}
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
              {t("family_title")}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">
              {t("family_subtitle")}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            data-testid="happy-family-showcase"
            className="mt-14 relative rounded-[2rem] overflow-hidden border border-border shadow-2xl shadow-brand-blue/10 bg-slate-950"
          >
            <img
              src="/tgs-family-v2.png"
              alt={t("family_img_alt")}
              className="block w-full h-auto max-h-[680px] object-contain sm:object-cover object-center bg-slate-950"
              loading="eager"
              fetchpriority="high"
            />
          </motion.div>
        </section>

        {/* Features */}
        <section className="mt-24 sm:mt-32">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-brand-gold">{t("why_eyebrow")}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              {t("why_title")}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.08 }}
                className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-brand-blue/40"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-blue/10 dark:bg-brand-blue/20 flex items-center justify-center text-brand-blue dark:text-blue-400 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  <f.icon className="h-6 w-6" />
                </div>
                <div className="mt-4 font-heading font-bold text-lg">{f.title}</div>
                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mt-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-brand-gold">{t("how_eyebrow")}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              {t("how_title")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 relative">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative rounded-2xl border border-border bg-card p-6"
              >
                <div className="font-heading font-black text-6xl text-brand-gold/30 leading-none">
                  {s.n}
                </div>
                <div className="mt-2 font-heading font-bold text-xl">{s.title}</div>
                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-24">
          <div className="relative rounded-3xl overflow-hidden border border-brand-blue/30 bg-gradient-to-br from-brand-blue via-brand-blue to-blue-900 p-8 sm:p-14 text-white">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-gold/30 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h3 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">
                  {t("cta_agent_title")}
                </h3>
                <p className="mt-2 text-white/80 max-w-lg">
                  {t("cta_agent_sub")}
                </p>
              </div>
              <Link to="/agent">
                <Button
                  data-testid="cta-agent-portal"
                  className="h-12 px-6 rounded-xl bg-brand-gold text-slate-900 hover:bg-brand-goldHover font-bold gap-2 shadow-xl"
                >
                  <Wallet className="h-4 w-4" /> {t("cta_agent_btn")} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-border/60 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <Logo size={32} showText={false} />
            <span className="text-muted-foreground">{fill(t("footer_copy"), { year: new Date().getFullYear() })}</span>
          </div>
          <div className="text-xs text-muted-foreground">{t("footer_tagline")}</div>
        </div>
      </footer>

      <WhatsAppAssistButton />
    </div>
  );
}
